/**
 * Claim flow: the user arrived with `?claim=<token>` in the URL. We
 * create fresh Ed25519 identity + auth keypairs, bind them to the
 * token via /auth/claim, and hand the in-memory identity + session
 * up to App.tsx. Persistence happens a step later, after the user
 * sets a passphrase — identity material must never be written to
 * IndexedDB in the clear (HIGH-06).
 */
import { toBase64Url } from '@chat/protocol';
import { useEffect, useRef, useState } from 'react';
import type { ChatClient } from '../client/api.js';
import { createIdentity, type Identity } from '../client/identity.js';

export interface ClaimHandlerProps {
  readonly client: ChatClient;
  readonly token: string;
  readonly onClaimed: (outcome: { identity: Identity; sessionToken: string }) => void;
}

export function ClaimHandler({ client, token, onClaimed }: ClaimHandlerProps) {
  const [status, setStatus] = useState<'claiming' | 'error'>('claiming');
  const [error, setError] = useState<string | undefined>();
  // Guard against React strict-mode double-invoke in dev.
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    void (async () => {
      try {
        const identity = createIdentity();
        const result = await client.claim({
          token,
          userId: identity.userId,
          deviceId: identity.deviceId,
          identityPublicKey: toBase64Url(identity.identityPublicKey),
          authPublicKey: toBase64Url(identity.authPublicKey),
        });
        // Scrub the magic-link token from the URL bar once it's been
        // consumed (LOW-17). Referrer-Policy already blocks Referer
        // leakage, but the URL still sits in the address bar + browser
        // sync history until the user navigates away.
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', '/');
        }
        onClaimed({ identity, sessionToken: result.token });
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'claim failed');
      }
    })();
  }, [client, token, onClaimed]);

  return (
    <main data-testid="claim" style={{ maxWidth: 480, margin: '3em auto', padding: '0 1em' }}>
      {status === 'claiming' && <p data-testid="claim-progress">setting up your account…</p>}
      {status === 'error' && (
        <section data-testid="claim-error">
          <h2>claim failed</h2>
          <p>{error}</p>
          <p>If the link has expired, request another by submitting your email on the homepage.</p>
        </section>
      )}
    </main>
  );
}
